import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { Plus, FileSignature } from 'lucide-react';
import { Button, Modal, EmptyState } from '../../common';
import MoneyDisplay from '../common/MoneyDisplay';
import ContractCard from './ContractCard';
import ContractForm, { type ContractFormHandle } from './ContractForm';
import { getContracts, createContract, deleteContract, getProcurements, createContractDocument, uploadFileToDocument } from '../../../services/dossier.service';
import type { DossierContract } from '../../../types';

interface Props {
  dossierId: number;
  projectId: number;
  summary: { total: number; totalValue: number; remainingValue: number; invoicedTotal: number };
  statusColor: 'green' | 'yellow' | 'gray' | 'red';
  onRefresh: () => void;
}

export default function ContracteSection({ dossierId, projectId, summary, statusColor, onRefresh }: Props) {
  const [contracts, setContracts] = useState<DossierContract[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [hasProcurements, setHasProcurements] = useState(true);
  const createFormRef = useRef<ContractFormHandle>(null);

  const loadContracts = useCallback(async () => {
    try {
      const data = await getContracts(dossierId);
      setContracts(data);
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut încărca contractele. Reîncarcă pagina și încearcă din nou.');
    }
    finally { setInitialLoading(false); }
  }, [dossierId]);

  useEffect(() => { loadContracts(); }, [loadContracts]);

  // Verificăm dacă dosarul are achiziții — pentru a ghida spre pasul anterior în empty-state
  useEffect(() => {
    getProcurements(dossierId)
      .then(procs => setHasProcurements((procs || []).length > 0))
      .catch(() => setHasProcurements(true));
  }, [dossierId]);

  const handleCreate = async (data: any) => {
    setCreating(true);
    try {
      // Separăm fișierele de datele contractului: se urcă abia după creare,
      // când avem id-ul contractului. Reutilizăm serviciile existente de documente.
      const { _files, ...contractData } = data;
      const resp: any = await createContract(dossierId, contractData);
      const newContractId = resp?.contract?.id;
      if (Array.isArray(_files) && _files.length > 0 && newContractId) {
        const docResp: any = await createContractDocument(newContractId, { document_name: 'Document contract', document_type: 'contract' });
        const newDocId = docResp?.document?.id;
        if (newDocId) {
          for (const file of _files) {
            await uploadFileToDocument(newContractId, newDocId, file);
          }
        }
      }
      setShowForm(false);
      await loadContracts();
      onRefresh();
      toast.success(
        Array.isArray(_files) && _files.length > 0
          ? `Contract adăugat, cu ${_files.length} document${_files.length > 1 ? 'e' : ''}.`
          : 'Contract adăugat cu succes.'
      );
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut salva contractul. Verifică datele și încearcă din nou.');
    } finally { setCreating(false); }
  };

  const handleDelete = async (contractId: number) => {
    try {
      await deleteContract(dossierId, contractId);
      await loadContracts();
      onRefresh();
      toast.success('Contract șters.');
    } catch (err) {
      console.error(err);
      toast.error('Nu am putut șterge contractul. Încearcă din nou.');
    }
  };

  const badge = summary.total > 0 ? `${summary.total} contracte` : '0 contracte';
  const badgeExtra = summary.totalValue > 0
    ? ` | Rest: ${new Intl.NumberFormat('ro-RO').format(summary.remainingValue)} RON`
    : '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600 font-medium">{badge}{badgeExtra}</div>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowForm(true)}
        >
          Adaugă contract
        </Button>
      </div>

      {/* Create Contract Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Contract nou"
        size="xl"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={() => setShowForm(false)}>Anulează</Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => createFormRef.current?.submit()}
              disabled={creating}
              isLoading={creating}
            >
              {creating ? 'Se creează...' : 'Creează contract'}
            </Button>
          </div>
        }
      >
        <ContractForm
          ref={createFormRef}
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          dossierId={dossierId}
          projectId={projectId}
          bare
        />
      </Modal>

      {initialLoading ? (
        <div className="text-center py-8 text-sm text-gray-500">Se încarcă...</div>
      ) : contracts.length === 0 ? (
        hasProcurements ? (
          <EmptyState
            icon={<FileSignature className="w-12 h-12 text-gray-400" />}
            title="Niciun contract încă"
            description="Contractele leagă achizițiile de livrabile și implementare. Adaugă primul contract al dosarului."
            action={{ label: 'Adaugă contract', onClick: () => setShowForm(true) }}
          />
        ) : (
          <EmptyState
            icon={<FileSignature className="w-12 h-12 text-gray-400" />}
            title="Niciun contract încă"
            description="Un contract provine, de regulă, dintr-o achiziție. Îți recomandăm să adaugi întâi o achiziție în pasul „Achiziții”. Poți crea totuși un contract și fără achiziție."
            action={{ label: 'Adaugă contract', onClick: () => setShowForm(true) }}
          />
        )
      ) : (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <div className="divide-y divide-gray-100">
            {contracts.map(contract => (
              <ContractCard
                key={contract.id}
                contract={contract}
                dossierId={dossierId}
                projectId={projectId}
                onRefresh={() => { loadContracts(); onRefresh(); }}
                onDelete={() => handleDelete(contract.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
